import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Timer, ArrowRight, Warning, Sparkle } from "@phosphor-icons/react";

/**
 * Bottleneck Spotlight — surfaces the slowest pipeline transition from the
 * time-to-hire funnel so recruiters can act on the single worst gap.
 *
 * Rendered on the recruiter dashboard between the pipeline strip and the jobs list.
 * Silently hides itself when there aren't enough hires yet (n < 3) or the funnel
 * data isn't available.
 */
const SEGMENT_LABEL = {
    applied_to_screening: "Applied → Screening",
    screening_to_interview: "Screening → Interview",
    interview_to_offer: "Interview → Offer",
    offer_to_hired: "Offer → Hired",
};

const SEGMENT_INSIGHT = {
    applied_to_screening: "Recruiters aren't triaging inbox fast enough. Enable the daily nudge digest or auto-reject stale rows.",
    screening_to_interview: "Hiring managers are slow to review shortlists. Loop them in via Slack/email digests.",
    interview_to_offer: "Panels are dragging on decisions. Add a decision-deadline nudge 24h after interview.",
    offer_to_hired: "Offers are sitting unsigned. Automate acceptance reminders + counter-offer templates.",
};

const asDays = (ms) => (ms == null ? null : ms / 86_400_000);

export default function BottleneckSpotlight() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get("/reports/time-to-hire")
            .then((r) => setData(r.data))
            .catch(() => setErr(true));
    }, []);

    if (err || !data) return null;
    if ((data.total_hires || 0) < 3) return null;  // not enough signal

    // Rank the 4 per-stage segments by median_ms; pick the slowest.
    const stages = ["applied_to_screening", "screening_to_interview", "interview_to_offer", "offer_to_hired"];
    const ranked = stages
        .map((k) => ({ key: k, ...(data.segments?.[k] || {}) }))
        .filter((s) => s.median_ms != null && s.count > 0)
        .sort((a, b) => b.median_ms - a.median_ms);
    if (ranked.length === 0) return null;

    const worst = ranked[0];
    const median = asDays(worst.median_ms);
    const p90 = asDays(worst.p90_ms);
    const overallMedian = asDays(data.segments?.applied_to_hired?.median_ms);
    // Percent of end-to-end median this stage consumes
    const sharePct = overallMedian ? Math.round((median / overallMedian) * 100) : null;

    const severity = median > 7 ? "high" : median > 3 ? "medium" : "low";
    const tone = severity === "high"
        ? { border: "border-[#DC2626]", bg: "bg-[#FEF2F2]", chip: "bg-[#DC2626] text-white", fg: "text-[#DC2626]" }
        : severity === "medium"
            ? { border: "border-[#F59E0B]", bg: "bg-[#FFFBEB]", chip: "bg-[#F59E0B] text-white", fg: "text-[#B45309]" }
            : { border: "border-cobalt", bg: "bg-cobalt/5", chip: "bg-cobalt text-white", fg: "text-cobalt" };

    return (
        <section
            data-testid="bottleneck-spotlight"
            className={`border-l-4 ${tone.border} ${tone.bg} p-4 sm:p-5 mb-8 sm:mb-10`}
        >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${tone.chip}`}>
                        <Warning size={18} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className={`text-[10px] font-black uppercase tracking-[0.25em] mb-1 inline-flex items-center gap-1.5 ${tone.fg}`}>
                            <Sparkle size={10} weight="fill" /> Bottleneck spotlight
                        </p>
                        <p className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-ink mb-1" data-testid="bottleneck-headline">
                            <span className={tone.fg}>{SEGMENT_LABEL[worst.key]}</span> is your slowest stage
                        </p>
                        <p className="text-xs text-[#525252] mb-3">
                            Median <strong className="font-mono text-ink">{median?.toFixed(1)}d</strong>
                            {" · "}p90 <strong className="font-mono text-ink">{p90?.toFixed(1)}d</strong>
                            {" · "}n={worst.count} hires
                            {sharePct && <> · {sharePct}% of your Applied→Hired time</>}
                        </p>
                        <p className="text-xs text-[#525252] leading-relaxed max-w-2xl" data-testid="bottleneck-insight">
                            {SEGMENT_INSIGHT[worst.key]}
                        </p>
                    </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0 sm:items-end">
                    <Link
                        to="/reports?report=time-to-hire"
                        data-testid="bottleneck-view-report"
                        className={`inline-flex items-center gap-1 border ${tone.border} ${tone.fg} hover:bg-white transition-colors px-3 py-2 text-[10px] font-black uppercase tracking-wider`}
                    >
                        <Timer size={11} weight="bold" /> View funnel <ArrowRight size={11} weight="bold" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
